import React, { useState, useEffect } from 'react';
import { ITEMS } from '../content/items';
import PLAYER from '../playerData';
import Shop from '../shop/Shop';

const Button = ({ onClick, children, disabled, className = '' }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`p-2 rounded font-game text-gray-300 bg-purple-700 m-1 cursor-pointer hover:bg-purple-600 ${className}`}
  >
    {children}
  </button>
);

const GameOverModal = ({ onFinishGame }) => (
  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-gray-800 p-8 rounded-lg text-center">
      <h2 className="text-2xl font-game text-gray-300 mb-4">
        Level Complete!
      </h2>
      <Button onClick={onFinishGame}>
        Continue
      </Button>
    </div>
  </div>
);

const LeaveConfirmationModal = ({ onStay, onLeave }) => (
  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-gray-800 p-8 rounded-lg text-center">
      <h2 className="text-2xl font-game text-gray-300 mb-4">
        Abandon Level?
      </h2>
      <p className="text-gray-400 mb-6">
        Your progress in this level will be lost.
      </p>
      <div className="flex gap-4 justify-center">
        <Button onClick={onStay} className="bg-gray-600 hover:bg-gray-500">
          Stay
        </Button>
        <Button onClick={onLeave} className="bg-red-600 hover:bg-red-500">
          Leave
        </Button>
      </div>
    </div>
  </div>
);

const ObjectiveDisplay = ({ objective, progress }) => (
  <div className="absolute top-4 left-0 right-0 bg-gray-800/90 px-4 py-3 mx-2 rounded-lg">
    <div className="text-center text-gray-300 font-game">
      <div className="text-sm opacity-75 mb-1">Objective:</div>
      <div className="flex items-center justify-center gap-2">
        <span className="text-3xl">{ITEMS[objective.itemId].emoji}</span>
        <span className="text-lg">{objective.description}</span>
      </div>
      <div className="text-xl font-bold mt-1">
        {progress.current} / {progress.target}
      </div>
    </div>
  </div>
);

const InventoryPanel = ({ inventory, onUseItem, onClose }) => (
  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-gray-800 p-6 rounded-lg w-5/6 max-h-[80%]">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-game text-gray-300">
          Inventory
        </h2>
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-white"
        >
          ✕
        </button>
      </div>
      
      <div className="overflow-y-auto max-h-[60vh]">
        {inventory.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            Your inventory is empty. Purchase items from the shop!
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {inventory.map((item, index) => (
              <div 
                key={`${item.id}-${index}`}
                className="bg-gray-700 rounded-lg p-3 flex items-center justify-between"
              >
                <div className="flex items-center">
                  <div className="text-2xl mr-3">{item.icon}</div>
                  <div>
                    <div className="font-bold">{item.name}</div>
                    <div className="text-sm text-gray-400">{item.description}</div>
                  </div>
                </div>
                <button
                  onClick={() => onUseItem(index)}
                  className="px-3 py-1 rounded bg-green-600 hover:bg-green-700"
                >
                  Use
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

function GameUI({ game, onFinishGame, onExit }) {
  const [, forceUpdate] = useState();
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
  const [showInventory, setShowInventory] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowInventory(game.showInventory);
      forceUpdate({});
    }, 10);
    return () => clearInterval(interval);
  }, []);

  const handleUseInventoryItem = (index) => {
    const success = game.useInventoryItem(index);
    if (success) {
      setMessage("Item used successfully!");
      setTimeout(() => setMessage(null), 2000);
    }
  };

  const objective = game.getLevelObjective();
  const progress = game.getCurrentProgress();

  return (
    <>
      {objective && progress && (
        <ObjectiveDisplay objective={objective} progress={progress} />
      )}
      
      <div className="absolute bottom-4 left-4 flex">
        <Button 
          onClick={() => setShowLeaveConfirmation(true)}
          className="bg-gray-600 hover:bg-gray-500"
        >
          Leave
        </Button>
        
        <Button 
          onClick={() => game.toggleInventory()}
          className="bg-blue-600 hover:bg-blue-700 ml-2"
        >
          Inventory {PLAYER.inventory.length > 0 && `(${PLAYER.inventory.length})`}
        </Button>
        
        <Button 
          onClick={() => setShowShop(true)}
          className="bg-green-600 hover:bg-green-700 ml-2"
        >
          Shop
        </Button>
      </div>

      {message && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 bg-green-600 px-4 py-2 rounded-lg text-white">
          {message}
        </div>
      )}

      {game.isWon && (
        <GameOverModal onFinishGame={onFinishGame} />
      )}

      {showLeaveConfirmation && (
        <LeaveConfirmationModal
          onStay={() => setShowLeaveConfirmation(false)}
          onLeave={onExit}
        />
      )}
      
      {showInventory && (
        <InventoryPanel 
          inventory={PLAYER.inventory}
          onUseItem={handleUseInventoryItem}
          onClose={() => game.toggleInventory()}
        />
      )}
      
      <Shop 
        isOpen={showShop} 
        onClose={() => setShowShop(false)} 
      />
    </>
  );
}

export default GameUI;
